// Asks the server's AI to read a document's text (worker/routes/ai.ts).
import type { AiSuggestions, ScanMode } from "../../shared/ai";
import type { CreditStatus, EarlyUserStatus } from "../../shared/ai-credits";
import { downgradeLine, graceLine } from "../../shared/inbox";
import { ApiError, api } from "./api";

export interface ScanInfo {
  mode: ScanMode;
  /** A quick scan that left parts of the document unread. */
  partial: boolean;
  totalChars: number;
  readChars: number;
}

/** Offered after a partial quick scan: what a full scan would cost and whether it fits. */
export interface FullScanOffer {
  estimate: number;
  needs: number;
  affordable: boolean;
}

export type AiOutcome =
  | {
      ok: true;
      suggestions: AiSuggestions;
      charged: number;
      credits: CreditStatus | null;
      scan: ScanInfo | null;
      fullScan: FullScanOffer | null;
      /** Credits we covered so the read could run. */
      grace: number;
      /** A full scan didn't fit, so a quick scan ran instead. */
      downgraded: { needs: number; had: number } | null;
    }
  /** quiet: AI isn't set up here (or the user skipped), so say nothing; otherwise show the message. */
  | { ok: false; quiet: boolean; message: string };

export async function readWithAi(text: string, signal?: AbortSignal, mode: ScanMode = "quick"): Promise<AiOutcome> {
  try {
    const r = await api<{
      suggestions: AiSuggestions;
      charged?: number;
      credits?: CreditStatus;
      scan?: ScanInfo;
      fullScan?: FullScanOffer | null;
      grace?: number;
      downgraded?: { needs: number; had: number } | null;
    }>("/api/ai/extract", {
      method: "POST",
      json: { text, mode },
      signal,
    });
    return {
      ok: true,
      suggestions: r.suggestions,
      charged: r.charged ?? 0,
      credits: r.credits ?? null,
      scan: r.scan ?? null,
      fullScan: r.fullScan ?? null,
      grace: r.grace ?? 0,
      downgraded: r.downgraded ?? null,
    };
  } catch (err) {
    if (signal?.aborted) return { ok: false, quiet: true, message: "Skipped." };
    if (err instanceof ApiError && err.code === "ai_not_configured") return { ok: false, quiet: true, message: err.message };
    return { ok: false, quiet: false, message: err instanceof Error ? err.message : "AI couldn't read the document." };
  }
}

export const getCredits = () => api<CreditStatus>("/api/ai/credits");

/** "Used 30 AI credits. About 2 reads left today." */
export function creditsLine(charged: number, credits: CreditStatus | null): string {
  if (!credits) return "";
  const used = charged ? `Used ${charged} AI credit${charged === 1 ? "" : "s"}. ` : "Already read before, so no credits used. ";
  const left = credits.readsLeft === 0 ? "No AI reads left for now." : `About ${credits.readsLeft} AI read${credits.readsLeft === 1 ? "" : "s"} left${credits.day.remaining <= credits.month.remaining ? " today" : " this month"}.`;
  return used + left;
}

/** "about 12% of it" for a partial quick scan. */
export function coverage(scan: Pick<ScanInfo, "readChars" | "totalChars">): string {
  const pct = scan.totalChars ? Math.max(1, Math.min(99, Math.round((scan.readChars / scan.totalChars) * 100))) : 100;
  return `about ${pct}% of it`;
}

/** "Early users: the first 50 get 10× free AI credits for a year." or null once the seats are gone. */
export function earlyUserOffer(s: EarlyUserStatus | undefined): string | null {
  if (!s?.active) return null;
  const left = s.spotsLeft <= 10 ? ` Only ${s.spotsLeft} spot${s.spotsLeft === 1 ? "" : "s"} left.` : "";
  return `Early users: the first ${s.seats} get ${s.multiplier}× free AI credits for a year.${left}`;
}

/** The kind extras after a read: grace credits we covered, or a full scan that became a quick one. */
export function aiExtras(result: { grace: number; downgraded: { needs: number; had: number } | null }): string {
  return [result.grace ? graceLine(result.grace) : "", result.downgraded ? downgradeLine(result.downgraded) : ""].filter(Boolean).join(" ");
}
